import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, MapPin, ArrowRight, BadgeCheck } from 'lucide-react';
import { Reveal } from '../motion';
import { discoveryCategories } from '../navConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FeaturedProviders = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/sponsored-providers`);
        const data = await res.json();
        setProviders(data.providers || []);
      } catch (e) {
        setProviders([]);
      }
    };
    load();
  }, []);

  return (
    <section
      data-testid="featured-providers-section"
      className="relative overflow-hidden bg-gray-50 dark:bg-gray-950 py-20 sm:py-24"
    >
      <span className="aurora-blob w-[26rem] h-[26rem] -bottom-40 end-[-6rem] bg-blue-300/40 dark:bg-blue-600/25 animate-aurora-slow" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
              {t('landing.featuredLabel', 'Featured')}
            </p>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">
              {t('landing.featuredTitle', 'Verified providers we love')}
            </h2>
            <p className="mt-3 text-base text-gray-600 dark:text-gray-400">
              {t('landing.featuredSubtitle', 'Handpicked professionals, licensed and reviewed by real customers')}
            </p>
          </div>
          <button
            onClick={() => navigate('/healthcare-providers')}
            data-testid="featured-view-all-button"
            className="group inline-flex items-center gap-2 self-start rounded-full border border-gray-200 dark:border-white/15 bg-white dark:bg-white/5 px-5 py-3 text-sm font-semibold text-gray-900 dark:text-white lift hover:border-blue-300 dark:hover:border-blue-400/40"
          >
            {t('landing.exploreAll', 'Explore all providers')}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180" />
          </button>
        </Reveal>

        {providers.length > 0 ? (
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {providers.map((provider, i) => (
              <Reveal key={provider.id} delay={i * 90}>
                <button
                  onClick={() => navigate(`/healthcare-providers/${provider.id}`)}
                  data-testid="provider-card-item"
                  className="group relative w-full text-start rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-6 lift hover:shadow-2xl hover:border-amber-300/80 dark:hover:border-amber-400/30"
                >
                  <span className="absolute top-5 end-5 inline-flex items-center gap-1 rounded-full bg-amber-400/95 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-950">
                    <BadgeCheck className="w-3 h-3" />
                    {provider.sponsored_label || t('landing.sponsored', 'Sponsored')}
                  </span>
                  <div className="flex items-center gap-4">
                    {provider.logo || provider.profile_image ? (
                      <img
                        src={provider.logo || provider.profile_image}
                        alt=""
                        loading="lazy"
                        className="w-14 h-14 rounded-2xl object-cover ring-1 ring-gray-200 dark:ring-white/10"
                        onError={(e) => {
                          e.target.style.visibility = 'hidden';
                        }}
                      />
                    ) : (
                      <span className="grid place-items-center w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-emerald-500 text-white text-lg font-bold">
                        {(provider.name || '?')[0]}
                      </span>
                    )}
                    <div className="min-w-0 pe-16">
                      <h3 className="font-display text-base font-bold text-gray-900 dark:text-white truncate">
                        {provider.name}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{provider.category}</p>
                    </div>
                  </div>
                  <div className="mt-5 flex items-center gap-4 text-xs">
                    {provider.average_rating || provider.rating ? (
                      <span className="inline-flex items-center gap-1 font-semibold text-gray-900 dark:text-white">
                        <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                        <span className="numeric-ltr">{provider.average_rating || provider.rating}</span>
                      </span>
                    ) : null}
                    {provider.area && (
                      <span className="inline-flex items-center gap-1 text-gray-500 dark:text-gray-400">
                        <MapPin className="w-3.5 h-3.5" />
                        {provider.area}
                      </span>
                    )}
                    <span className="ms-auto inline-flex items-center gap-1 font-semibold text-blue-600 dark:text-blue-400">
                      {t('hero.bookNow', 'Book Now')}
                      <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180" />
                    </span>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {discoveryCategories.map((cat, i) => {
              const Icon = cat.icon;
              return (
                <Reveal key={cat.id} delay={i * 60}>
                  <button
                    onClick={() => navigate(cat.to)}
                    data-testid={`browse-chip-${cat.id}`}
                    className="w-full rounded-2xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-5 text-center lift hover:shadow-xl"
                  >
                    <span className={`mx-auto grid place-items-center w-11 h-11 rounded-2xl ${cat.chip}`}>
                      <Icon className={`w-5 h-5 ${cat.accent}`} />
                    </span>
                    <span className="mt-3 block text-xs font-semibold text-gray-800 dark:text-gray-200">
                      {t(cat.labelKey, cat.fallback)}
                    </span>
                  </button>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedProviders;
